import { StateService } from '@uirouter/angular';
import { Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { EdlPublicComponent } from './layout/public/edl-public.component';
import { EdlAuthenticatedNavigationComponent } from './layout/authenticated/edl-authenticated-navigation.component';
import {EdlPrintComponent} from './layout/print/edl-print.component';
import { AccessRoleHelper } from './model/helpers/access-role.helper';
import { PortalHelpersService } from './services/portal-helpers/portal-helpers.service';
import { SessionService, UserService, PageNotFoundComponent, UnauthorizedComponent, GenericErrorPageComponent } from '@edl-development/edl-common';
import { AuthenticatedHeaderComponent } from './layout/authenticated/authenticated-header/authenticated-header.component';
import { AuthenticatedComponent } from './layout/authenticated/authenticated/authenticated.component';

export function onEnterAuthenticated(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);

    if (!sessionService.isAuthenticated()) {
        sessionService.redirectState = {
            name: trans.$to().name,
            params: trans.params()
        };

        return trans.router.stateService.target('public.login');
    }
}

export function onEnterDashboard(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);
    var stateService: StateService = trans.injector().get(StateService);
    var portalHelpersService: PortalHelpersService = trans.injector().get(PortalHelpersService);

    if (!sessionService.isAuthenticated()) {
        return trans.router.stateService.target('public.login');
    }     else   if (sessionService.redirectState) {
        let redirect: { name: string, params: any } = sessionService.redirectState;
        sessionService.redirectState = null;
        stateService.go(redirect.name, redirect.params, {reload: true});
    } else {

        const portalSettings = sessionService.user.PortalSettings;
        const isAct48HoursOnly = portalHelpersService.isAct48HoursOnly(portalSettings);

        if (AccessRoleHelper.isLimitedSupervisor(sessionService.user.AccessRoleID)) {
            if(isAct48HoursOnly) {
                return trans.router.stateService.target('authenticated.requests.sm.search', { type:'myrequests', page: 1 }, {reload: true});
            } else {
                return trans.router.stateService.target('authenticated.supervision-management.my-supervision-items.all', { page: 1 }, {reload: true});
            }
        }
        else {
            if(isAct48HoursOnly) {
                return trans.router.stateService.target('authenticated.requests.my-requests', { page: 1 }, {reload: true});
            } else {
                return trans.router.stateService.target('authenticated.my-compliance-items.all', { page: 1 }, {reload: true});
            }
        }

    }
}

export function onEnterLogout(trans: Transition) {
    const sessionService: SessionService = trans.injector().get(SessionService);

    // Need to include any custom options that were passed to the transition
    const custom = trans?.options()?.custom;

    // Destroy the session
    sessionService.destroy();

    // Redirect to the login page
    return trans.router.stateService.target('public.login', {}, { custom });
}

export function onEnterSwitchPortals(transition: Transition) {
    return transition.router.stateService.target('authenticated.dashboard');
}

export function resolveSwitchPortals(transition: Transition, userService: UserService) {
    const params = transition.params();
    const portalId: number = params.portalId;

    return userService.
        switchPortal(portalId)
        .toPromise();
}

export const EdlComplyStates: Ng2StateDeclaration[] = [{
    name: 'public',
    abstract: true,
    data: {
        isPublic: true
    },
    views: {
        'base': {
            component: EdlPublicComponent
        }
    }
}, {
    name: 'public.page-not-found',
    url: '/404',
    views: {
        'content@public': {
            component: PageNotFoundComponent
        }
    }
}, {
    name: 'public.unauthorized',
    url: '/401',
    views: {
        'content@public': {
            component: UnauthorizedComponent
        }
    }
}, {
    name: 'public.generic-error',
    url: '/500',
    views: {
        'content@public': {
            component: GenericErrorPageComponent
        }
    }
}, {
    name: 'public.logout',
    onEnter: onEnterLogout
}, {
    name: 'authenticated',
    abstract: true,
    views: {
        'base': {
            component: AuthenticatedComponent
        },
        'header@authenticated': {
            component: AuthenticatedHeaderComponent
        },
        'navigation@authenticated': {
            component: EdlAuthenticatedNavigationComponent
        }
    },
    onEnter: onEnterAuthenticated
}, {
    name: 'authenticated.dashboard',
    url: '/',
    onEnter: onEnterDashboard
}, {
    name: 'authenticated.switch-portals',
    url: '/switch-portals/:portalId',
    onEnter: onEnterSwitchPortals,
    resolve: [{
        token: 'portalData',
        deps: [Transition, UserService],
        resolveFn: resolveSwitchPortals
    }]
}, {
    name: 'print',
    abstract: true,
    url: '/print',
    views: {
        'base': {
            component: EdlPrintComponent
        }
    }
},

{
  name: 'public.user-agreement.**',
  url: '/user-agreement',
  loadChildren: () => import('./user-agreement/user-agreement.module').then(mod => mod.UserAgreementModule)
},

// FUTURE STATES
{
    name: 'authenticated.portal.**',
    url: '/portalmanagement',
    loadChildren: () => import('./portal/portal.module').then(mod => mod.PortalModule)
}, {
    name: 'authenticated.reports.**',
    url: '/reporting-management',
    loadChildren: () => import('./reports/reports.module').then(mod => mod.ReportsModule)
}, {
    name: 'authenticated.groups.**',
    url: '/groups',
    loadChildren: () => import('./groups/groups.module').then(mod => mod.GroupsModule)
}, {
    name: 'authenticated.users.**',
    url: '/users',
    loadChildren: () => import('./users/users.module').then(mod => mod.UsersModule)
}, {
    name: 'authenticated.questionnaires.**',
    url: '/questionnaires',
    loadChildren: () => import('./questionnaires/questionnaires.module').then(mod => mod.QuestionnairesModule)
}, {
    name: "authenticated.requests.**",
    url: "/requests",
    loadChildren: () => import('./requests/requests.module').then(mod => mod.RequestsModule)
}, {
    name: 'authenticated.my-compliance-items.**',
    url: '/my-compliance-items',
    loadChildren: () => import('./my-compliance-items/my-compliance-items.module').then(mod => mod.MyComplianceItemsModule)
}, {
    name: 'authenticated.supervision-management.**',
    url: '/supervision-management',
    loadChildren: () => import('./supervision-management/supervision-management.module').then(mod => mod.SupervisionManagementModule)
}, {
    name: 'authenticated.messages.**',
    url: '/messages?page&start&end&searchBy',
    loadChildren: () => import('./messages/messages.module').then(mod => mod.MessagesModule)
}];
